import { useEffect, useState } from "react";
import QuestionBox from "../../ui/QuestionBox/QuestionBox";
import RadioButton from "../../ui/RadioButton/RadioButton";
import classes from "../Infrastructure/Infrastructure.module.css";
import CommonFooter from "../../CommonFooter/CommonFooter";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { env } from "../../../constants/env";
import { Bounce, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import CommonButton from "../../ui/CommonButton/CommonButton";

const initialState = {
  receivesCommunication: "",
  sendsReportsToHigherLevel: "",
  perfectCommunicationWithDistrictHealthOffices: "",
  perfectCommunicationWithProvincialHealthOffices: "",
  perfectCommunicationWithMinistryOfHealth: "",
  communicationChallengesAndComments: "",
  communicatesWithHealthPost: "",
  communicatesWithHealthCenters: "",
  communicatesWithSatelliteFacilities: "",
  communicationWithDistrictHealthOffices: "",
  communicationWithProvincialHealthOffices: "",
  communicationWithMinistryHealthServices: "",
};

const Reports = () => {
  const [currentSet, setCurrentSet] = useState(0);
  const { user } = useAuth();

  const { labId: paramLabId } = useParams();
  const labId = paramLabId || user?.labId;

  const userRole = user?.role;

  const [formData, setFormData] = useState(initialState);
  const [ownerLabId, setOwnerLabId] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const isReadOnly = userRole !== "LABORATORY" || !editMode;

  const sectionName = "communication"; // formType

  useEffect(() => {
    if (labId) {
      fetchQuestionnaire();
    }
  }, [labId]);

  const fetchQuestionnaire = async () => {
    try {
      setIsFetching(true);
      const res = await axios.get(
        `${env.kataApiUrl}/api/questionnaire/${labId}/${sectionName}`
      );

      if (res.data) {
        if (res.data.answers) {
          setFormData(res.data.answers);
          setEditMode(false); // ✅ answers exist → read-only by default
        } else {
          setFormData(initialState);
          setEditMode(true); // ✅ no answers → open in edit mode
        }
        setOwnerLabId(res.data.labId);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("No existing questionnaire found for this lab.");
        setFormData(initialState);
        setEditMode(true); // ✅ new lab → start in edit mode
      } else {
        console.error("Error fetching questionnaire:", error);
        toast.error("Failed to load saved questionnaire");
      }
    } finally {
      setIsFetching(false);
    }
  };

  // --- pass disabled to inputs ---
  const handleChange = (field, value) => {
    if (isReadOnly) return; // block edits
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (section, key, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const handleDone = async () => {
    try {
      setLoading(true);
      const payload = {
        labId,
        formType: sectionName,
        answers: formData,
      };

      const res = await axios.post(
        `${env.kataApiUrl}/api/questionnaire/${sectionName}`,
        payload
      );

      toast.success("Submitted successfully!");

      fetchQuestionnaire();
      setCurrentSet(0);
      setEditMode(false); // exit edit mode

      console.log(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const questionSets = [
    {
      title: "Communication and reports",
      subtitle: "A. Communication and reports with higher levels",
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox
            number={1}
            question="The laboratory receives communication"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="receivesCommunication"
                checked={formData.receivesCommunication === "Yes"}
                onChange={() => handleChange("receivesCommunication", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="receivesCommunication"
                checked={formData.receivesCommunication === "No"}
                onChange={() => handleChange("receivesCommunication", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={2}
            question="The laboratory sends reports to higher level"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="sendsReportsToHigherLevel"
                checked={formData.sendsReportsToHigherLevel === "Yes"}
                onChange={() =>
                  handleChange("sendsReportsToHigherLevel", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="sendsReportsToHigherLevel"
                checked={formData.sendsReportsToHigherLevel === "No"}
                onChange={() => handleChange("sendsReportsToHigherLevel", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={3}
            question="Perfect communication with the District Health Offices"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="perfectCommunicationWithDistrictHealthOffices"
                checked={
                  formData.perfectCommunicationWithDistrictHealthOffices ===
                  "Yes"
                }
                onChange={() =>
                  handleChange(
                    "perfectCommunicationWithDistrictHealthOffices",
                    "Yes"
                  )
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="perfectCommunicationWithDistrictHealthOffices"
                checked={
                  formData.perfectCommunicationWithDistrictHealthOffices ===
                  "No"
                }
                onChange={() =>
                  handleChange(
                    "perfectCommunicationWithDistrictHealthOffices",
                    "No"
                  )
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={4}
            question="Perfect communication with the Provincial Health Offices"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="perfectCommunicationWithProvincialHealthOffices"
                checked={
                  formData.perfectCommunicationWithProvincialHealthOffices ===
                  "Yes"
                }
                onChange={() =>
                  handleChange(
                    "perfectCommunicationWithProvincialHealthOffices",
                    "Yes"
                  )
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="perfectCommunicationWithProvincialHealthOffices"
                checked={
                  formData.perfectCommunicationWithProvincialHealthOffices ===
                  "No"
                }
                onChange={() =>
                  handleChange(
                    "perfectCommunicationWithProvincialHealthOffices",
                    "No"
                  )
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={5}
            question="Perfect communication with Ministry of Health/Medical Services Department"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="perfectCommunicationWithMinistryOfHealth"
                checked={
                  formData.perfectCommunicationWithMinistryOfHealth === "Yes"
                }
                onChange={() =>
                  handleChange(
                    "perfectCommunicationWithMinistryOfHealth",
                    "Yes"
                  )
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="perfectCommunicationWithMinistryOfHealth"
                checked={
                  formData.perfectCommunicationWithMinistryOfHealth === "No"
                }
                onChange={() =>
                  handleChange("perfectCommunicationWithMinistryOfHealth", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={6}
            question="Challenges and comments"
            placeholder="Add comment"
            multilineInput
            value={formData.communicationChallengesAndComments}
            onChange={(e) =>
              handleChange("communicationChallengesAndComments", e.target.value)
            }
            disabled={isReadOnly}
          />
        </div>
      ),
    },
    {
      title: "Communication and reports",
      subtitle: "B. Communication and reports to lower levels",
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox number={1} question="Health Post" disabled={isReadOnly}>
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="communicatesWithHealthPost"
                checked={formData.communicatesWithHealthPost === "Yes"}
                onChange={() =>
                  handleChange("communicatesWithHealthPost", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="communicatesWithHealthPost"
                checked={formData.communicatesWithHealthPost === "No"}
                onChange={() =>
                  handleChange("communicatesWithHealthPost", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox number={2} question="Health Centers">
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="communicatesWithHealthCenters"
                checked={formData.communicatesWithHealthCenters === "Yes"}
                onChange={() =>
                  handleChange("communicatesWithHealthCenters", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="communicatesWithHealthCenters"
                checked={formData.communicatesWithHealthCenters === "No"}
                onChange={() =>
                  handleChange("communicatesWithHealthCenters", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox number={3} question="Satellite Facilities">
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="communicatesWithSatelliteFacilities"
                checked={formData.communicatesWithSatelliteFacilities === "Yes"}
                onChange={() =>
                  handleChange("communicatesWithSatelliteFacilities", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="communicatesWithSatelliteFacilities"
                checked={formData.communicatesWithSatelliteFacilities === "No"}
                onChange={() =>
                  handleChange("communicatesWithSatelliteFacilities", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={4}
            question="Perfect Communication with the District Health Offices"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="communicationWithDistrictHealthOffices"
                checked={
                  formData.communicationWithDistrictHealthOffices === "Yes"
                }
                onChange={() =>
                  handleChange("communicationWithDistrictHealthOffices", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="communicationWithDistrictHealthOffices"
                checked={
                  formData.communicationWithDistrictHealthOffices === "No"
                }
                onChange={() =>
                  handleChange("communicationWithDistrictHealthOffices", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={5}
            question="Perfect Communication with the Provincial Health Offices"
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="communicationWithProvincialHealthOffices"
                checked={
                  formData.communicationWithProvincialHealthOffices === "Yes"
                }
                onChange={() =>
                  handleChange(
                    "communicationWithProvincialHealthOffices",
                    "Yes"
                  )
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="communicationWithProvincialHealthOffices"
                checked={
                  formData.communicationWithProvincialHealthOffices === "No"
                }
                onChange={() =>
                  handleChange("communicationWithProvincialHealthOffices", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={6}
            question="Perfect Communication with Ministry /Health Services Department"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="communicationWithMinistryHealthServices"
                checked={
                  formData.communicationWithMinistryHealthServices === "Yes"
                }
                onChange={() =>
                  handleChange("communicationWithMinistryHealthServices", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="communicationWithMinistryHealthServices"
                checked={
                  formData.communicationWithMinistryHealthServices === "No"
                }
                onChange={() =>
                  handleChange("communicationWithMinistryHealthServices", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
        </div>
      ),
    },
  ];

  const { subtitle, content } = questionSets[currentSet];

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
     <div
      className={`${classes.wrapper} ${
        paramLabId ? classes.fromParams : ""
      } ${isReadOnly ? classes.readOnly : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h2>Communication and reports</h2>
        {userRole === "LABORATORY" && !editMode && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditMode(true)}
            title="Edit"
          />
        )}
      </div>
      <h4>{subtitle}</h4>
      {(!editMode && userRole !== "MINISTRY") ? (
        <div className={classes.infoText}>
          ✅ Your communication and reports has been submitted.
        </div>
      ) : (
        content
      )}
      {!isReadOnly && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditMode(false);
            fetchQuestionnaire();
            setCurrentSet(0);
          }}
          questionSets={questionSets}
          currentSet={currentSet}
          disabled={loading}
          doneText={loading ? "Submitting..." : "Submit"}
        />
      )}
    </div>
  );
};

export default Reports;
